import { useEffect, useState } from "react";
import { Create, required, SelectInput, SimpleForm, useNotify, useRedirect } from "react-admin";
import { CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";
import BackButtonToolbar from "features/BackButtonToolbar";
import { SectionTitle } from "features/SectionTitle";
import { doctoralRequests } from "api/doctoralApi";
import { DoctoralServicePath } from "utils/constants";
import ReadOnlySection from "./ReadOnlySection";
import PersonalInfoSection from "./PersonalInfoSection";
import NamesEnSection from "./NamesEnSection";
import BirthSection from "./BirthSection";
import PreviousEducationSection from "./PreviousEducationSection";
import TopicAndSupervisorsSection from "./TopicAndSupervisorsSection";
import { DoctoralSchoolPaymentProviderChoices } from "utils/choices";

const listPath = `/${DoctoralServicePath}approved-candidacies`;

const toDateOnly = value => (value ? value.split("T")[0] : null);

const EnrollDoctorant = () => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const redirect = useRedirect();
  const location = useLocation();

  const { doctorantCandidacyId, doctoralSeatId } = location.state || {};
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!doctorantCandidacyId || !doctoralSeatId) {
      redirect(listPath);
      return;
    }
    const fetchEnrollment = async () => {
      try {
        const res = await dataProvider.customRequest(
          doctoralRequests.getDoctorantEnrollment(doctorantCandidacyId, doctoralSeatId),
        );
        if (res?.data?.isEnrolled) {
          notify("Кандидатът вече е записан", { type: "info" });
          redirect(listPath);
          return;
        }
        setData(res?.data);
      } catch (err) {
        notifyError(err);
        redirect(listPath);
      }
    };

    fetchEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <CircularProgress sx={{ margin: "3rem auto", display: "block" }} />;

  const transform = form => ({
    doctorantCandidacyId,
    doctoralSeatId,
    topic: form.topic,
    orderNumber: form.orderNumber,
    orderDate: toDateOnly(form.orderDate),
    supervisorEmployeeIds: (form.supervisors ?? []).map(s => s?.employeeId).filter(Boolean),
    paymentProviderId: form.paymentProviderId,
    userInfo: {
      firstName: form.firstName,
      secondName: form.secondName,
      lastName: form.lastName,
      firstNameEn: form.firstNameEn,
      secondNameEn: form.secondNameEn,
      lastNameEn: form.lastNameEn,
      gender: form.gender,
      birthDate: toDateOnly(form.birthDate),
      birthCountryId: form.birthCountryId,
      birthSettlementId: form.birthSettlementId,
      birthSettlement: form.birthSettlement,
      contactPhone: form.contactPhone,
    },
    previousEducation: {
      ...form.previousEducation,
      diplomaDate: toDateOnly(form.previousEducation?.diplomaDate),
    },
  });

  const candidate = data.candidate || {};

  return (
    <Create
      resource={doctoralRequests.enrollDoctorant()}
      title="Форма за записване"
      transform={transform}
      mutationOptions={{
        onSuccess: () => {
          notify("Докторантът е записан успешно", { type: "success" });
          redirect(listPath);
        },
        onError: err => {
          notifyError(err);
        },
      }}
      actions={<BackButtonToolbar />}
    >
      <SimpleForm
        defaultValues={{
          firstName: candidate.firstName,
          secondName: candidate.secondName,
          lastName: candidate.lastName,
          firstNameEn: candidate.firstNameEn,
          secondNameEn: candidate.secondNameEn,
          lastNameEn: candidate.lastNameEn,
          gender: candidate.gender,
          birthDate: candidate.birthDate,
          birthCountryId: candidate.birthCountryId,
          birthSettlementId: candidate.birthSettlementId,
          birthSettlement: candidate.birthSettlement,
          contactPhone: candidate.contactPhone,
          previousEducation: data.previousEducation || {},
          topic: "",
          supervisors: [{ employeeId: null }],
        }}
      >
        <SectionTitle paddingY={2} title="Данни за записването" />
        <ReadOnlySection data={data} />

        <SectionTitle paddingY={2} title="Лични данни" />
        <PersonalInfoSection />

        <SectionTitle paddingY={2} title="Имена (латиница)" />
        <NamesEnSection />

        <SectionTitle paddingY={2} title="Месторождение" />
        <BirthSection />

        <SectionTitle paddingY={2} title="Предходно образование" />
        <PreviousEducationSection />

        <SectionTitle paddingY={2} title="Докторантура" />
        <TopicAndSupervisorsSection />

        <SectionTitle paddingY={2} title="Плащане на годишната такса" />
        <SelectInput
          source="paymentProviderId"
          label="Начин на плащане"
          choices={DoctoralSchoolPaymentProviderChoices}
          sx={{ minWidth: 320 }}
          validate={required()}
        />
      </SimpleForm>
    </Create>
  );
};

export default EnrollDoctorant;
